import React, { useEffect, useRef, useState } from 'react';
import { SearchInput } from '../SearchInput';
import { Button, Modal } from '../ui';
import {
  DEFAULT_SHARING_FILTERS,
  SHARING_CATEGORIES,
  SHARING_LANGUAGES,
  SharingCategory,
  SharingFilters,
  SharingLanguage,
  sharingFiltersCount,
} from '../../utils/sharing';

interface SharingCatalogBarProps {
  initialQuery: string;
  onQueryChange: (q: string) => void;
  searchFocused: boolean;
  onSearchFocus: (focused: boolean) => void;
  category: SharingCategory;
  onCategoryChange: (c: SharingCategory) => void;
  filters: SharingFilters;
  onFiltersChange: (f: SharingFilters) => void;
  /** Верх ползунка цены */
  priceCeiling: number;
  /** Меняется при «Сбросить» — поле поиска пересоздается пустым */
  resetKey?: number;
}

/**
 * Верхняя панель раздела «Шеринг» — та же, что в каталоге: поиск, раздел и фильтры.
 * Региона нет: позиции шеринга продаются в рублях без регионов.
 */
export const SharingCatalogBar: React.FC<SharingCatalogBarProps> = ({
  initialQuery,
  onQueryChange,
  searchFocused,
  onSearchFocus,
  category,
  onCategoryChange,
  filters,
  onFiltersChange,
  priceCeiling,
  resetKey = 0,
}) => {
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState<SharingFilters>(filters);
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeFiltersCount = sharingFiltersCount(filters);
  const activeCategoryLabel = SHARING_CATEGORIES.find((c) => c.value === category)?.label || 'Все';
  const draftMaxPrice = Math.min(draftFilters.maxPrice ?? priceCeiling, priceCeiling);

  const togglePlatform = (platform: 'PS4' | 'PS5') => {
    setDraftFilters((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const toggleLanguage = (language: SharingLanguage) => {
    setDraftFilters((prev) => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter((l) => l !== language)
        : [...prev.languages, language],
    }));
  };

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters });
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    onFiltersChange(draftFilters);
    setShowFilters(false);
  };

  return (
    <>
      {/* Search + Category + Filters — sticky bar, как в каталоге */}
      <div className="sticky top-0 z-20 bg-white px-3 pt-2 pb-1 relative" style={{ transform: 'translateZ(0)' }}>
        <div className="flex items-center gap-1.5">
          <SearchInput
            key={resetKey}
            initialValue={initialQuery}
            onQueryChange={onQueryChange}
            onFocusChange={onSearchFocus}
            focused={searchFocused}
            debounceMs={200}
          />

          {!searchFocused && (
            <div className="relative flex-shrink-0" ref={categoryRef}>
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className={`flex items-center gap-1 px-2.5 py-2 rounded-[14px] text-sm font-medium transition-all whitespace-nowrap ${
                  category !== 'all'
                    ? 'bg-primary text-white'
                    : 'bg-background-secondary'
                }`}
                style={category === 'all' ? { color: '#1A1D1A' } : undefined}
              >
                <span className="max-w-[72px] truncate">{activeCategoryLabel}</span>
                <svg
                  className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${showCategoryDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full right-0 mt-1.5 bg-white rounded-[14px] border border-gray-200/80 z-30 min-w-[160px] animate-fade-in overflow-hidden">
                  {SHARING_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => { onCategoryChange(cat.value); setShowCategoryDropdown(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        category === cat.value
                          ? 'text-primary font-semibold bg-primary-50'
                          : 'text-text-primary hover:bg-background-secondary'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!searchFocused && (
            <button
              onClick={handleOpenFilters}
              className="relative flex items-center justify-center transition-all flex-shrink-0"
              style={{
                padding: '8px',
                borderRadius: '14px',
                background: activeFiltersCount > 0 ? 'rgba(54, 87, 240, 0.08)' : '#F4F4F7',
              }}
              aria-label="Фильтры"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21.25 12H8.895M4.534 12H2.75M4.534 12C4.534 11.4218 4.76368 10.8673 5.17251 10.4585C5.58134 10.0497 6.13583 9.82 6.714 9.82C7.29217 9.82 7.84666 10.0497 8.25549 10.4585C8.66432 10.8673 8.894 11.4218 8.894 12C8.894 12.5782 8.66432 13.1327 8.25549 13.5415C7.84666 13.9503 7.29217 14.18 6.714 14.18C6.13583 14.18 5.58134 13.9503 5.17251 13.5415C4.76368 13.1327 4.534 12.5782 4.534 12ZM21.25 18.607H15.502M15.502 18.607C15.502 19.1853 15.2718 19.7404 14.8628 20.1493C14.4539 20.5583 13.8993 20.788 13.321 20.788C12.7428 20.788 12.1883 20.5573 11.7795 20.1485C11.3707 19.7397 11.141 19.1852 11.141 18.607M15.502 18.607C15.502 18.0287 15.2718 17.4746 14.8628 17.0656C14.4539 16.6567 13.8993 16.427 13.321 16.427C12.7428 16.427 12.1883 16.6567 11.7795 17.0655C11.3707 17.4743 11.141 18.0288 11.141 18.607M11.141 18.607H2.75M21.25 5.393H18.145M13.784 5.393H2.75M13.784 5.393C13.784 4.81483 14.0137 4.26033 14.4225 3.8515C14.8313 3.44268 15.3858 3.213 15.964 3.213C16.2503 3.213 16.5338 3.26938 16.7983 3.37894C17.0627 3.4885 17.3031 3.64907 17.5055 3.8515C17.7079 4.05394 17.8685 4.29426 17.9781 4.55875C18.0876 4.82324 18.144 5.10672 18.144 5.393C18.144 5.67928 18.0876 5.96276 17.9781 6.22725C17.8685 6.49174 17.7079 6.73206 17.5055 6.93449C17.3031 7.13692 17.0627 7.2975 16.7983 7.40705C16.5338 7.51661 16.2503 7.573 15.964 7.573C15.3858 7.573 14.8313 7.34332 14.4225 6.93449C14.0137 6.52566 13.784 5.97117 13.784 5.393Z" stroke="#1A1D1A" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round"/>
              </svg>
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filters Modal — как в каталоге, без региона */}
      <Modal
        isOpen={showFilters}
        onClose={() => { setDraftFilters({ ...filters }); setShowFilters(false); }}
        title="Фильтры"
        dismissable={false}
        footer={
          <div className="space-y-1.5">
            <Button variant="primary" size="sm" fullWidth onClick={handleApplyFilters}>Применить</Button>
            <Button variant="secondary" size="sm" fullWidth onClick={() => setDraftFilters(DEFAULT_SHARING_FILTERS)}>Очистить все фильтры</Button>
          </div>
        }
      >
        <div className="space-y-3.5">
          {/* Platform */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-2">Платформа</h3>
            <div className="grid grid-cols-2 gap-2">
              {(['PS4', 'PS5'] as const).map((platform) => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-center ${
                    draftFilters.platforms.includes(platform)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200'
                  }`}
                  style={!draftFilters.platforms.includes(platform) ? { color: '#1A1D1A' } : undefined}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-0.5">Цена</h3>
            <p className="text-sm mb-1.5" style={{ color: '#1A1D1A' }}>
              0 ₽ – {draftMaxPrice} ₽
            </p>
            <input
              type="range"
              min="0"
              max={priceCeiling}
              step="100"
              value={draftMaxPrice}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setDraftFilters((prev) => ({ ...prev, maxPrice: value >= priceCeiling ? null : value }));
              }}
              className="w-full accent-primary"
            />
          </div>

          {/* Language */}
          <div>
            <h3 className="text-[15px] font-bold text-text-primary mb-2">Язык</h3>
            <div className="flex flex-col gap-1.5">
              {SHARING_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => toggleLanguage(lang.value)}
                  className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border-[1.5px] transition-all text-sm font-medium text-left ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200'
                  }`}
                  style={!draftFilters.languages.includes(lang.value) ? { color: '#1A1D1A' } : undefined}
                >
                  <span className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 ${
                    draftFilters.languages.includes(lang.value)
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                  }`}>
                    {draftFilters.languages.includes(lang.value) && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
